import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      email: (email || '').trim().toLowerCase(),
      password,
    });
    const { token: newToken, user_id } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    await axios.put(`${API}/auth/profile`, profileData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    await fetchUser();
  };

  const authorize = async (authData) => {
    const response = await axios.post(`${API}/auth/authorize`, authData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    await fetchUser();
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateProfile,
    authorize,
    fetchUser,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.role === 'admin',
    isPremium: user?.subscription_plan === 'premium'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
